import axios from "axios";
import { MediaUploadManager } from "./mediaUploadManager";
import { archiveStore } from "./archiveStore";
import { compressImage } from "./imageCompression";
import { originalSha256 } from "./originalHasher";
import { putMedia } from "./uploadTransport";
import { registerMedia } from "./mediaReferences";
import { servableCopyCount } from "./mediaPresets";
import type { MediaApi, MediaUploadStatus } from "./mediaTypes";
import { collectorAuthHeaders, getCollectorToken, onCollectorSessionCleared } from "../helpers/collectorSession";
import { withCollectorSession } from "../services/collectorApi";

const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "") + "/collector/media";

function payload<T>(response: any): T {
  if (!response?.success || response.data == null) throw new Error(response?.message || "Media service unavailable");
  return response.data as T;
}

/** Plain words for a collector who has hit the daily photo limit. */
export const COLLECTOR_PHOTO_QUOTA_MESSAGE = "You've added a lot of photos today. Try again tomorrow.";

/**
 * Plain words for a collector whose photo space is full. The cap is flat (not tied to the plan), and photos
 * that are on no piece are cleared after about a week, which gives their space back. No upgrade link.
 */
export const COLLECTOR_PHOTO_STORAGE_MESSAGE =
  "Your photo space is full. Photos you remove are cleared within a week, then you can add more.";

/** A save referred to a photo that was uploaded but cleaned up before the piece was saved (after 7 days). */
export const COLLECTOR_PHOTO_EXPIRED_MESSAGE = "One photo was too old to save. Please add it again.";

/** The photo could not be sent because the sign-in ended; the person signs in again. */
export const COLLECTOR_PHOTO_SESSION_MESSAGE = "Please log in again to add photos.";

/** Generic copy for any other upload failure. */
export const COLLECTOR_PHOTO_FAILED_MESSAGE = "The photo did not upload. Check your internet and try again.";

/** A media service error that keeps the HTTP status, the step and the server code, so callers can choose a fallback. */
export class CollectorMediaError extends Error {
  constructor(message: string, readonly status: number | undefined, readonly step: string, readonly code?: string) {
    super(message);
    this.name = "CollectorMediaError";
  }
}

async function call<T>(request: Promise<{ data: unknown }>, step: string): Promise<T> {
  try {
    return payload<T>((await request).data);
  } catch (error: any) {
    const status: number | undefined = error?.response?.status;
    const code: string | undefined = error?.response?.data?.message || undefined;
    let message: string;
    // The per-account daily quota answers init with 429 (or 413 from some proxies), or names itself.
    if ((step === "init" && (status === 429 || status === 413)) || code === "COLLECTOR_PHOTO_DAILY_LIMIT") {
      message = COLLECTOR_PHOTO_QUOTA_MESSAGE;
    } else if (status === 403 && code === "COLLECTOR_PHOTO_STORAGE_FULL") {
      message = COLLECTOR_PHOTO_STORAGE_MESSAGE;
    } else {
      message = code || error?.message || "Media service unavailable";
    }
    throw new CollectorMediaError(message, status, step, code);
  }
}

const isSessionProblem = (error: any): boolean =>
  error?.status === 401 || error?.response?.status === 401 ||
  /sign in to resume/i.test(String(error?.message || ""));

/**
 * What to tell a collector when a photo did not upload: never a raw code or axios text.
 * `kind` lets a screen react (for example send the person to sign in on "session").
 */
export function collectorPhotoProblem(error: unknown): { kind: "quota" | "storage" | "session" | "other"; message: string } {
  const message = (error as { message?: string })?.message;
  if (message === COLLECTOR_PHOTO_QUOTA_MESSAGE) return { kind: "quota", message };
  if (message === COLLECTOR_PHOTO_STORAGE_MESSAGE) return { kind: "storage", message };
  if (isSessionProblem(error)) return { kind: "session", message: COLLECTOR_PHOTO_SESSION_MESSAGE };
  return { kind: "other", message: COLLECTOR_PHOTO_FAILED_MESSAGE };
}

/**
 * A piece save the server refused because a photo it names was already cleaned up: 410 Gone with
 * PHOTO_EXPIRED (older servers sent PHOTO_EXPIRED with 400/401). A bare 410 with no code of its own counts too.
 */
export const isPhotoExpiredError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
  const message = response?.data?.message;
  if (message === "PHOTO_EXPIRED") return true;
  return response?.status === 410 && (!message || /^(?:410\s+)?gone\.?$/i.test(message.trim()));
};

/** Every media call is signed with the collector token: an expired session renews once and the call is retried. */
const signed = <R>(request: () => Promise<R>) =>
  withCollectorSession(request, (error: any) => error?.response?.status === 401);

const post = <T>(path: string, input: unknown = {}, step = "upload") =>
  call<T>(signed(() => axios.post(base() + path, input, { headers: collectorAuthHeaders() })), step);

/** The studio media API, signed with the collector's session instead. Originals are never downloadable here. */
export const collectorMediaApi: MediaApi = {
  init: input => post("/init", { ...input, purpose: "COLLECTOR_PIECE" }, "init"),
  masterUpload: id => post("/" + encodeURIComponent(id) + "/master/upload"),
  completeMaster: id => post("/" + encodeURIComponent(id) + "/master/complete"),
  startOriginal: id => post("/" + encodeURIComponent(id) + "/original/multipart/start"),
  presignPart: (id, partNumber) => post("/" + encodeURIComponent(id) + "/original/multipart/parts", { partNumber }),
  completeOriginal: id => post("/" + encodeURIComponent(id) + "/original/multipart/complete"),
  downloadOriginal: () => Promise.reject(new Error("Originals are kept in the archive")),
  get: id => call(signed(() => axios.get(base() + "/" + encodeURIComponent(id), { headers: collectorAuthHeaders() })), "get"),
};

/** Archive jobs belong to one collector account; another sign-in on this phone never resumes them. */
export function collectorMediaOwnerKey(): string {
  const token = getCollectorToken();
  if (!token) return "";
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), "=")));
    return claims?.collectorId ? "collector:" + claims.collectorId : "";
  } catch {
    return "";
  }
}

export const collectorMediaUploadManager = new MediaUploadManager({
  api: collectorMediaApi, store: archiveStore, ownerKey: collectorMediaOwnerKey,
  compress: compressImage, hash: originalSha256, put: putMedia,
});

const PROGRESS: Partial<Record<MediaUploadStatus, (p: number) => number>> = {
  OPTIMIZING_MASTER: () => 5,
  MASTER_UPLOADING: p => 10 + Math.round(p * 0.85),
  OPERATIONAL_READY: () => 100,
};

let queue: Promise<unknown> = Promise.resolve();

/**
 * Uploads one photo through the media service and returns its durable "hkp-media:<id>" reference.
 * Photos go one at a time, so progress events belong to the photo being sent. The original keeps
 * archiving in the background; the piece can be saved as soon as this resolves.
 */
export function uploadCollectorMediaPhoto(file: File, onProgress?: (percent: number) => void): Promise<string> {
  const run = async () => {
    const unsubscribe = collectorMediaUploadManager.subscribe((_id, status, progress) => {
      const map = PROGRESS[status];
      if (map) onProgress?.(map(progress));
    });
    try {
      const { mediaId, media, disposePreview } = await collectorMediaUploadManager.upload(file, "COLLECTOR_PIECE");
      disposePreview();
      // Copies made already let HKPImage show "hkp-media:<id>" at the right size; never the master.
      if (servableCopyCount(media) > 0) registerMedia(media);
      return "hkp-media:" + mediaId;
    } finally {
      unsubscribe();
    }
  };
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
}

/** Account whose unfinished archives were already picked up in this tab, so later page changes do nothing. */
let resumedFor = "";

/**
 * Resume original archives a collector left unfinished (page closed mid-upload). Runs once per account per tab:
 * moving between My Collection pages neither restarts nor stops archiving.
 */
export function resumeCollectorArchives(): void {
  const owner = collectorMediaOwnerKey();
  if (!owner || resumedFor === owner) return;
  resumedFor = owner;
  void collectorMediaUploadManager.restore()
    .then(jobs => jobs.forEach(job => { void collectorMediaUploadManager.resume(job.mediaId).catch(() => undefined); }))
    .catch(() => undefined);
}

/**
 * The session ended (sign-out, refused renewal, number change): stop archiving for this account now and forget
 * that this tab resumed it. Unfinished jobs stay saved and resume at its next sign-in.
 */
export function stopCollectorArchives(): void {
  resumedFor = "";
  collectorMediaUploadManager.interruptAll();
}

// However the collector session ends, archiving for it stops here.
onCollectorSessionCleared(() => stopCollectorArchives());
